import { Fragment } from 'react';
import { CheckIcon } from 'lucide-react';
import type {
  DocumentGenerationProgressDTO,
  DocumentGenerationStatusResponseDTO,
} from '@shared/schemas/document-generation';
import { cn } from '@/lib/utils';
import { buildGenerationProgress, type GenerationStep } from '../document-canvas/generation-progress';
import { useResearchStageSeen } from '../hooks/useResearchStageSeen';
import './document-generation-card.css';

type DocumentGenerationStepperProps = {
  className?: string;
  generationId?: string;
  progress: DocumentGenerationProgressDTO | undefined;
  status?: DocumentGenerationStatusResponseDTO['status'];
};

function StepMarker({ state }: { state: GenerationStep['state'] }) {
  return (
    <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
      {state === 'done' ? (
        <span className="docgen-step-check flex size-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <CheckIcon className="size-2.5" strokeWidth={3.5} />
        </span>
      ) : state === 'active' ? (
        <span className="docgen-step-disc size-3.5 rounded-full bg-primary" />
      ) : (
        <span className="size-3.5 rounded-full border-[1.5px] border-muted-foreground/45" />
      )}
    </span>
  );
}

// Largeur (du stepper lui-même) à partir de laquelle tous les libellés tiennent : mesurée au rendu
// (4 étapes ≈ 24,5 rem, 5 étapes avec « Recherche » ≈ 32 rem). En dessous, seule l'étape courante garde le sien.
const LABEL_CLASS_BY_STEP_COUNT: Record<number, string> = {
  4: '@[26rem]:inline',
  5: '@[33rem]:inline',
};
const CONNECTOR_CLASS_BY_STEP_COUNT: Record<number, string> = {
  4: '@[26rem]:mx-3.5 @[26rem]:w-[13px]',
  5: '@[33rem]:mx-3.5 @[33rem]:w-[13px]',
};

/**
 * Barre fine + étapes « Plan — [Recherche —] Rédaction — Mise en forme — Prêt » de la carte de génération.
 * Fidèle à la maquette : étapes groupées à gauche, reliées par de courts tirets de largeur fixe.
 * « Recherche » n'apparaît que si la génération est passée par cette étape (cf. `useResearchStageSeen`).
 * Le compteur de sections n'est pas affiché dans la carte (il reste dans l'annonce vocale
 * et dans le bandeau du canvas) : la maquette garde l'étape lisible d'un coup d'œil.
 */
export function DocumentGenerationStepper({ className, generationId, progress, status }: DocumentGenerationStepperProps) {
  const withResearch = useResearchStageSeen(generationId, progress);
  const view = buildGenerationProgress(progress, status, { withResearch });
  const isActive = view.percent < 100;
  const labelClass = LABEL_CLASS_BY_STEP_COUNT[view.steps.length];
  const connectorClass = CONNECTOR_CLASS_BY_STEP_COUNT[view.steps.length];

  return (
    <div className={cn('@container flex min-w-0 flex-col', className)} data-testid="generation-progress">
      {/* Seule l'étape courante est annoncée : le compteur change trop souvent pour un lecteur d'écran. */}
      <p className="sr-only" role="status">{view.currentLabel}</p>
      <div
        aria-label="Progression de la génération"
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={view.percent}
        aria-valuetext={view.currentLabel}
        className="docgen-track"
        role="progressbar"
      >
        <div
          className="docgen-fill"
          data-active={isActive}
          style={{ width: `${Math.max(view.percent, 2)}%` }}
        />
      </div>
      <ol aria-hidden="true" className="mt-3 flex min-w-0 items-center">
        {view.steps.map((step, index) => (
          <Fragment key={step.key}>
            {index > 0 ? (
              <li
                aria-hidden
                className={cn('docgen-connector mx-1 h-px w-1 min-w-1 shrink-0 bg-muted-foreground/25', connectorClass)}
                role="presentation"
              />
            ) : null}
            <li
              className={cn(
                'docgen-step flex shrink-0 items-center gap-2 text-[11.5px] leading-none transition-colors duration-300',
                step.state === 'active' && 'font-medium text-foreground',
                step.state === 'done' && 'text-muted-foreground',
                step.state === 'todo' && 'text-muted-foreground/65',
              )}
              data-state={step.state}
            >
              <StepMarker state={step.state} />
              {/* Étroit : seule l'étape courante garde son libellé, les pastilles suffisent aux autres. */}
              <span className={cn(step.state !== 'active' && 'hidden', labelClass)}>
                {step.label}
              </span>
            </li>
          </Fragment>
        ))}
      </ol>
    </div>
  );
}
